import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"
import DatalakeService from "src/services/datalake-service"
import dayjs from "dayjs"
import { DATE_FORMAT } from "src/utils/constants"

const ParameterDatalakeSpreadsEmissaoTable = (props) => {
    const dataLakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj, index) => ({ ...obj, id: index }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const createRow = async () => {
        const body = {
            CETIP: 'N/A',
            FluxoValidadeInicial: dayjs().format(DATE_FORMAT),
            TipoAtivo: 'N/A',
            Indicador: 'N/A',
            TaxaAccrual: 0,
            Rating: 'N/A',
            IDCurvaRiskFree: 0,
            IDCurvaPRE: 0,
            DurationAccrual: 0,
            TaxaRiskFree: 0,
            TaxaCurvaRating: 0,
            TIR_Accrual: 0,
            SpreadCreditoFinal: 0,
            SpreadRelativoFinal: 0,
            LOGERRO: 'N/A',
            SpreadCreditoComIR : null,
            SpreadCreditoSemIR : null,
            SpreadRelativoComIR : null,
            SpreadRelativoSemIR : null
        }

        props.loading(true)
        return dataLakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData()
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, CETIP, FluxoValidadeInicial, TipoAtivo, Indicador, TaxaAccrual, Rating, IDCurvaRiskFree,
            IDCurvaPRE, DurationAccrual, TaxaRiskFree, TaxaCurvaRating, TIR_Accrual, SpreadCreditoEmissao,
            SpreadRelativoEmissao, LOGERRO, SpreadCreditoComIR, SpreadCreditoSemIR, SpreadRelativoComIR, SpreadRelativoSemIR  } = newRow

        const body = {
            CETIP: CETIP,
            FluxoValidadeInicial: FluxoValidadeInicial,
            TipoAtivo: TipoAtivo,
            Indicador: Indicador,
            TaxaAccrual: TaxaAccrual,
            Rating: Rating,
            IDCurvaRiskFree: IDCurvaRiskFree,
            IDCurvaPRE: IDCurvaPRE,
            DurationAccrual: DurationAccrual,
            TaxaRiskFree: TaxaRiskFree,
            TaxaCurvaRating: TaxaCurvaRating,
            TIR_Accrual: TIR_Accrual,
            SpreadCreditoEmissao: SpreadCreditoEmissao,
            SpreadRelativoEmissao: SpreadRelativoEmissao,
            LOGERRO: LOGERRO,
            SpreadCreditoComIR : SpreadCreditoComIR,
            SpreadCreditoSemIR : SpreadCreditoSemIR,
            SpreadRelativoComIR : SpreadRelativoComIR,
            SpreadRelativoSemIR : SpreadRelativoSemIR
        }

        props.loading(true)
        return dataLakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return dataLakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        dataLakeService.paginate(endpoint).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'FluxoValidadeInicial',
            headerName: 'FluxoValidadeInicial',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'date',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'TipoAtivo',
            headerName: 'TipoAtivo',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'Indicador',
            headerName: 'Indicador',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'TaxaAccrual',
            headerName: 'TaxaAccrual',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            typeComponent: 'number',
            type: 'number',
            editable: true
        },
        {
            field: 'Rating',
            headerName: 'Rating',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'IDCurvaRiskFree',
            headerName: 'IDCurvaRiskFree',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'IDCurvaPRE',
            headerName: 'IDCurvaPRE',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'DurationAccrual',
            headerName: 'DurationAccrual',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'TaxaRiskFree',
            headerName: 'TaxaRiskFree',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'TaxaCurvaRating',
            headerName: 'TaxaCurvaRating',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'TIR_Accrual',
            headerName: 'TIR_Accrual',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'SpreadCreditoFinal',
            headerName: 'SpreadCreditoFinal',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'SpreadRelativoFinal',
            headerName: 'SpreadRelativoFinal',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            typeComponent: 'number',
            type: 'number',
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'LOGERRO',
            headerName: 'LOGERRO',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'SpreadCreditoComIR ',
            headerName: 'SpreadCreditoComIR',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'SpreadCreditoSemIR',
            headerName: 'SpreadCreditoSemIR',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'SpreadRelativoComIR ',
            headerName: 'SpreadRelativoComIR',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'SpreadRelativoSemIR',
            headerName: 'SpreadRelativoSemIR',
            flex: 1,
            minWidth: 150,
            maxWidth: 350,
            disableColumnMenu: true,
            editable: true
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                customFilter={true} />
        </div>
    )
}

ParameterDatalakeSpreadsEmissaoTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
}

export default ParameterDatalakeSpreadsEmissaoTable
